import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api, authUrls } from '~/api/client';
import type { CurrentSession } from '~/api/types';

/**
 * Who is signed in, resolved once for the whole application.
 *
 * `GET /v1/me` is the only call that can answer this, because the session lives in an HttpOnly
 * cookie the browser cannot read. A 401 is therefore not an error but the expected answer for a
 * visitor, and it is treated as such: signed-out is a state, not a failure.
 */

interface SessionContextValue {
  readonly session: CurrentSession | undefined;
  readonly isLoading: boolean;
  readonly isSignedIn: boolean;
  readonly hasCloudflareAccount: boolean;
  readonly isDeveloper: boolean;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
  /** Marketplace sign-in (Cloudflare). */
  signIn(returnTo?: string): void;
  /** Re-authorize / add Cloudflare accounts while already signed in. */
  connectCloudflare(returnTo?: string): void;
  /** Attach GitHub for publishing (requires an existing session). */
  connectGithub(returnTo?: string): void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<CurrentSession | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const current = await api.session(signal);
      setSession(current);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      // Anything other than "not signed in" is still rendered as signed-out: the shell must load
      // even when the identity service is briefly unavailable.
      setSession(undefined);
      if (!(cause instanceof ApiError)) throw cause;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      isLoading,
      isSignedIn: session !== undefined,
      hasCloudflareAccount: (session?.cloudflareAccounts.length ?? 0) > 0,
      isDeveloper: session?.developer !== undefined,
      async refresh() {
        await load();
      },
      async signOut() {
        await api.signOut().catch(() => undefined);
        setSession(undefined);
      },
      signIn(returnTo?: string) {
        window.location.assign(authUrls.cloudflareSignIn(returnTo ?? currentPath()));
      },
      connectCloudflare(returnTo?: string) {
        window.location.assign(authUrls.connectCloudflare(returnTo ?? currentPath()));
      },
      connectGithub(returnTo?: string) {
        window.location.assign(authUrls.connectGithub(returnTo ?? currentPath()));
      },
    }),
    [session, isLoading, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === undefined) {
    throw new Error('useSession must be used inside a SessionProvider.');
  }
  return value;
}

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}
